import { Link } from "react-router-dom";
import Seo from "@/components/Seo";
import { Cookie, ShieldCheck, Database, Eye } from "lucide-react";

export default function CookiePolicy() {
    return (
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 py-14" data-testid="cookie-policy-page">
            <Seo
                title="Cookie- og privatlivspolitik"
                description="Vi respekterer dit privatliv. Løftebarometeret bruger ikke traditionelle cookies og sælger ikke data. Kun anonymt localStorage — ingen trackere, ingen reklamer."
                keywords="cookie politik, privatliv, GDPR, databeskyttelse, localStorage, persondata"
                path="/cookies"
            />
            <header className="mb-10">
                <div className="flex items-center gap-2 mb-2">
                    <Cookie className="w-5 h-5 text-royal" />
                    <span className="text-xs uppercase tracking-[0.2em] text-[#4F5A7A] font-semibold">
                        Privatliv
                    </span>
                </div>
                <h1 className="font-serif-dm text-4xl sm:text-5xl text-royal mb-3">
                    Cookie- og privatlivspolitik
                </h1>
                <p className="text-[#4F5A7A] leading-relaxed">
                    Sidst opdateret: 22. februar 2026. Vi respekterer dit privatliv.
                    Denne side forklarer klart, hvilke data vi bruger — og hvorfor.
                </p>
            </header>

            <section className="space-y-8 text-[#0B1A3A] leading-relaxed">
                <Block
                    icon={ShieldCheck}
                    title="Det korte svar"
                >
                    <p>
                        Løftebarometeret <strong>bruger ikke traditionelle cookies</strong> og
                        <strong> sælger ikke data</strong>. Vi benytter udelukkende{" "}
                        <em>localStorage</em> i din browser — og kun til nødvendige og
                        anonyme funktioner på sitet.
                    </p>
                </Block>

                <Block icon={Database} title="Hvilke data gemmes lokalt i din browser?">
                    <ul className="space-y-3 list-disc pl-5">
                        <li>
                            <strong>loftebarometer_voter_token</strong> — et tilfældigt uuid
                            som forhindrer, at samme bruger stemmer flere gange i en afstemning.
                            Indeholder ingen personoplysninger.
                        </li>
                        <li>
                            <strong>loftebarometer_visitor_id</strong> — et tilfældigt uuid
                            til vores unikke besøgstæller. Bruges kun til den samlede tæller
                            i footeren og er fuldstændig anonym.
                        </li>
                        <li>
                            <strong>loftebarometer_admin_token</strong> — kun hvis du er
                            administrator og har indtastet et token på <code>/admin</code>.
                            Alm. besøgende har ikke dette.
                        </li>
                    </ul>
                </Block>

                <Block icon={Eye} title="Hvad sender vi til serveren?">
                    <ul className="space-y-3 list-disc pl-5">
                        <li>
                            Et anonymt <code>visitor_id</code> — så vi kan tælle unikke
                            besøgende uden at spore dig på tværs af sites.
                        </li>
                        <li>
                            Din IP-adresse og User-Agent logges <strong>kun</strong> ved fejlede
                            admin-login-forsøg (brute force-beskyttelse). Slettes automatisk
                            efter 15 min, hvis ingen nye forsøg.
                        </li>
                        <li>
                            <strong>Ingen tredjeparts-trackere</strong>. Ingen Google Analytics,
                            ingen Facebook-pixel, ingen reklame-cookies.
                        </li>
                    </ul>
                </Block>

                <Block icon={ShieldCheck} title="Dit samtykke">
                    <p className="mb-3">
                        Første gang du besøger sitet vises et banner i bunden. Du kan:
                    </p>
                    <ul className="space-y-2 list-disc pl-5">
                        <li>
                            <strong>Accepter</strong> — vi må gemme et anonymt visitor-id og
                            tælle dig med i besøgsstatistikken.
                        </li>
                        <li>
                            <strong>Kun nødvendige</strong> — vi gemmer kun voter-token
                            (påkrævet for afstemninger) og springer besøgstælleren over.
                        </li>
                    </ul>
                    <p className="mt-4">
                        Du kan til enhver tid ændre dit valg ved at rydde din browsers
                        localStorage for dette site.
                    </p>
                </Block>

                <Block icon={Database} title="Tredjepartskilder">
                    <p>
                        Løftebarometeret henter offentligt tilgængelig data fra:
                    </p>
                    <ul className="space-y-2 list-disc pl-5 mt-3">
                        <li>ft.dk (Folketingets OData API)</li>
                        <li>DR Nyheder, TV 2, Politiken, B.T., Ekstra Bladet (RSS-feeds)</li>
                        <li>Google News (fallback-søgning)</li>
                    </ul>
                    <p className="mt-3">
                        Din besøg på vores site deler <strong>ingen data</strong> med disse
                        kilder.
                    </p>
                </Block>

                <Block icon={ShieldCheck} title="Dine rettigheder">
                    <p>
                        Da vi ikke gemmer personhenførbare data om dig, er der som udgangspunkt
                        ikke noget at anmode om indsigt i eller slette. Skulle du alligevel have
                        spørgsmål, er du velkommen til at kontakte systemejer via{" "}
                        <a
                            href="https://simonkarlsson.com"
                            className="text-royal underline hover:text-royal-deep"
                            target="_blank"
                            rel="noreferrer noopener"
                        >
                            simonkarlsson.com
                        </a>
                        .
                    </p>
                </Block>
            </section>

            <div className="mt-12 text-sm">
                <Link to="/" className="text-royal hover:text-royal-deep underline">
                    ← Tilbage til forsiden
                </Link>
            </div>
        </div>
    );
}

function Block({ icon: Icon, title, children }) {
    return (
        <div className="bg-white border border-[#E5E8F0] rounded-2xl p-6">
            <h2 className="flex items-center gap-2 font-serif-dm text-2xl text-royal mb-4">
                <Icon className="w-5 h-5" /> {title}
            </h2>
            <div className="text-[#4F5A7A] space-y-2">{children}</div>
        </div>
    );
}
